"use client"
import * as React from 'react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { redirectToSessionRefresh, redirectToForbiddenLogin, UnauthorizedError, ForbiddenError, apiFetch, setAuthRedirectConfig } from '../backend/utils/api'

interface PatchedWindow extends Window {
  __omFetchPatched?: boolean
  __omOriginalFetch?: typeof window.fetch
}

type ErrorWithStatus = { status?: number }

// Ensure global fetch calls also respect our redirect-on-401/403 policy.
function ensureGlobalFetchInterception() {
  if (typeof window === 'undefined') return
  const w = window as PatchedWindow
  if (w.__omFetchPatched) return
  w.__omFetchPatched = true
  w.__omOriginalFetch = window.fetch
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => apiFetch(input, init)
}

const client = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof UnauthorizedError) redirectToSessionRefresh()
      else if (error instanceof ForbiddenError) redirectToForbiddenLogin()
      // As a fallback, try to detect common cases
      else if ((error as ErrorWithStatus)?.status === 401) redirectToSessionRefresh()
      else if ((error as ErrorWithStatus)?.status === 403) redirectToForbiddenLogin()
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (error instanceof UnauthorizedError) redirectToSessionRefresh()
      else if (error instanceof ForbiddenError) redirectToForbiddenLogin()
      else if ((error as ErrorWithStatus)?.status === 401) redirectToSessionRefresh()
      else if ((error as ErrorWithStatus)?.status === 403) redirectToForbiddenLogin()
    },
  }),
})

type QueryProviderProps = { children: React.ReactNode; defaultForbiddenRoles?: string[] }

export function QueryProvider({ children, defaultForbiddenRoles }: QueryProviderProps) {
  React.useEffect(() => {
    ensureGlobalFetchInterception()
    if (defaultForbiddenRoles && defaultForbiddenRoles.length) {
      setAuthRedirectConfig({ defaultForbiddenRoles })
    }
  }, [])
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}
